import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EpisodeCard from '@/components/feature/EpisodeCard';
import { useHistory } from '@/hooks/useFavorites';
import { useSeo } from '@/hooks/useSeo';
import { formatDate, getDramaById, getEpisodeById, getEpisodesByDrama } from '@/lib/content';
import Player from './components/Player';
import EpisodeSwitcher from './components/EpisodeSwitcher';

export default function Play() {
  const { episodeId = '' } = useParams();
  const episode = getEpisodeById(episodeId);
  const drama = episode ? getDramaById(episode.dramaId) : undefined;
  const { pushHistory } = useHistory();

  useSeo({
    title:
      episode && drama
        ? `迷人的保姆《${drama.title}》第${episode.number}集在线观看演示 - ${episode.title}`
        : '在线播放演示 - 迷人的保姆在线观看',
    description:
      episode && drama
        ? `《${drama.title}》第${episode.number}集 ${episode.title} 在线播放演示页面，包含剧情简介、集数切换与章节信息。`
        : '迷人的保姆剧集在线播放演示页面。',
    keywords: '迷人的保姆播放,电视剧在线观看,剧集在线播放,迷人的保姆在线观看',
  });

  useEffect(() => {
    if (episode) pushHistory(episode.id);
  }, [episode, pushHistory]);

  if (!episode || !drama) {
    return (
      <div className="w-full px-4 md:px-8 lg:px-12 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="text-xl font-semibold text-foreground-900">内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-600">未找到对应的播放章节，可能链接有误。</p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap"
        >
          返回剧集分类
        </Link>
      </div>
    );
  }

  const episodes = getEpisodesByDrama(drama.id);
  const moreEpisodes = episodes.filter((e) => e.aired && e.id !== episode.id).slice(0, 4);

  return (
    <div className="w-full bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12 py-5 border-b border-background-200">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '剧集分类', to: '/category' },
            { label: drama.title, to: `/detail/${drama.id}` },
            { label: `第 ${episode.number} 集` },
          ]}
        />
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 py-6 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 lg:gap-8 items-start">
          <div className="min-w-0">
            <Player episode={episode} dramaTitle={drama.title} />

            <div className="mt-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-accent-500 text-background-50 dark:text-foreground-950">
                  {episode.aired ? `第 ${episode.number} 集` : '待更新'}
                </span>
                {episode.isNew && (
                  <span className="px-2.5 py-1 rounded-full text-[11px] bg-primary-100 text-primary-700">最新更新</span>
                )}
              </div>
              <h1 className="mt-3 text-xl md:text-2xl font-bold text-foreground-950">
                {drama.title} · {episode.title}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  时长 {episode.aired ? episode.duration : '未播出'}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  更新于 {episode.aired ? formatDate(episode.publishedAt) : '待定'}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-movie-2-line"></i>
                  {drama.genres.join(' · ')}
                </span>
              </div>

              <div className="mt-6 rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
                  <i className="ri-book-open-line text-primary-700"></i>
                  本集简介
                </h2>
                <p className="mt-2.5 text-sm text-foreground-700 leading-relaxed">{episode.summary}</p>
                <p className="mt-4 text-xs text-foreground-500 leading-relaxed flex items-start gap-1.5">
                  <i className="ri-information-line mt-0.5"></i>
                  温馨提示：本页播放区域为前端功能演示，用于展示观看界面与交互效果，不提供真实视频源。
                </p>
              </div>
            </div>

            <div className="mt-8">
              <div className="flex items-center justify-between gap-4 mb-4">
                <h2 className="text-base font-bold text-foreground-950 flex items-center gap-2">
                  <i className="ri-play-list-line text-accent-600"></i>
                  继续观看其它章节
                </h2>
                <Link
                  to={`/detail/${drama.id}`}
                  className="text-sm text-primary-700 hover:text-primary-600 inline-flex items-center gap-1 whitespace-nowrap"
                >
                  全部剧集
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {moreEpisodes.map((item) => (
                  <EpisodeCard key={item.id} episode={item} dramaTitle={drama.title} />
                ))}
              </div>
            </div>
          </div>

          <aside className="w-full lg:sticky lg:top-24 rounded-lg bg-background-50 border border-background-200 p-5">
            <h2 className="text-sm font-semibold text-foreground-950 flex items-center gap-2 mb-4">
              <i className="ri-list-check text-primary-700"></i>
              剧集目录
            </h2>
            <EpisodeSwitcher episodes={episodes} currentId={episode.id} />

            <div className="mt-6 pt-5 border-t border-background-200">
              <h3 className="text-sm font-semibold text-foreground-950 mb-3">剧集信息</h3>
              <dl className="space-y-2.5 text-xs">
                <div className="flex gap-2">
                  <dt className="text-foreground-500 w-14 shrink-0">导演</dt>
                  <dd className="text-foreground-800">{drama.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 w-14 shrink-0">主演</dt>
                  <dd className="text-foreground-800">{drama.cast.slice(0, 3).join('、')}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 w-14 shrink-0">更新</dt>
                  <dd className="text-foreground-800">
                    至 {drama.updatedEpisodes} / {drama.totalEpisodes} 集
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-foreground-500 w-14 shrink-0">安排</dt>
                  <dd className="text-foreground-800">{drama.weekUpdate}</dd>
                </div>
              </dl>
              <Link
                to={`/detail/${drama.id}`}
                className="mt-5 inline-flex items-center justify-center gap-1.5 w-full h-10 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看剧集详情
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}